import React from 'react';

import Tippy from '@tippyjs/react';

import { cn, ia, iaRa } from 'lib/helpers/utility';

import Tag from 'components/shared/Tag/Tag';

const JUSTIFY_CLASS = { start: 'justify-start', center: 'justify-center', end: 'justify-end' };

/**
 * Standalone, pass `items`. As an AG Grid cellRenderer (no `items`) it reads the cell `value`,
 * shows the key on its own group rows and keeps the chips on one line, vertically centered.
 *
 * @param {import('./lib/propTypes').OverflowChipListProps} OverflowChipListProps
 */

const OverflowChipList = ({
  items,
  value,
  node,
  emptyText,
  visibleCount = 1,
  showAllUpTo,
  chipClassName = 'min-w-0 max-w-[160px]',
  className,
  justify = items === undefined ? 'start' : 'center'
}) => {
  const isCell = items === undefined;
  // Group rows: where this column's group label is drawn, `value` is the key string; in every other
  // cell of the row the valueGetter has no data and yields an empty list.
  if (isCell && node?.group) return typeof value === 'string' ? value : null;

  const finalItems = isCell ? iaRa(value).filter(Boolean) : iaRa(items);

  if (!ia(finalItems)) {
    return emptyText ? <span className="text-xs text-neutral-500">{emptyText}</span> : null;
  }

  const shownCount = finalItems.length <= showAllUpTo ? finalItems.length : visibleCount;
  const safeVisibleCount = Math.max(1, shownCount);
  const visible = finalItems.slice(0, safeVisibleCount);
  const rest = finalItems.slice(safeVisibleCount);

  const list = (
    <div
      className={cn(
        'flex flex-row flex-wrap items-center gap-1',
        JUSTIFY_CLASS[justify],
        isCell && 'min-w-0 flex-nowrap',
        className
      )}>
      {visible.map((item, index) => (
        <Tippy
          key={`${item}-${index}`}
          content={<span className="text-white">{item}</span>}
          theme="dark"
          appendTo={() => document.body}>
          {/* Tippy needs a DOM ref, which Tag doesn't forward. */}
          <span className={cn('flex', chipClassName)}>
            <Tag truncate className="min-w-0">
              {item}
            </Tag>
          </span>
        </Tippy>
      ))}

      {ia(rest) && (
        <Tippy
          className="custom-tooltip tippy-dark !rounded-md"
          allowHTML
          interactive
          appendTo={() => document.body}
          offset={[0, 10]}
          content={
            <div className="flex-column p-2">
              {rest.map((item, index) => (
                <div key={`${item}-${index}`} className="text-slate-50 m-1 rounded-md p-1">
                  {item}
                </div>
              ))}
            </div>
          }>
          <span className="flex shrink-0">
            <Tag>+{rest.length}</Tag>
          </span>
        </Tippy>
      )}
    </div>
  );

  if (!isCell) return list;

  return (
    <div className={cn('flex h-full min-w-0 items-center overflow-hidden', JUSTIFY_CLASS[justify])}>
      {list}
    </div>
  );
};

export default React.memo(OverflowChipList);
